import { Userpilot } from 'userpilot';
import { DashboardTypeEnum } from '@/enums/dashboardTypes.js';
import { GetEmployeeSingle } from '@/services/Employees/GetEmployeeSingle';
import dayjs from 'dayjs';

export const USERPILOT_STG_KEY = 'STG-NX-4c59f21e';
export const USERPILOT_PROD_KEY = 'NX-4c59f21e';
declare global {
  interface ImportMetaEnv {
    MODE: string;
  }
  
  interface ImportMeta {
    env: ImportMetaEnv;
  }
}
interface User {
  id: string;
  userName?: string;
  dashboardType?: number; // Based on the enum definition
  clientUrl?: string;
}
interface BasicInformation {
  sproutUserId?: string;
  employeeId?: string;
}
interface WorkInformation {
  departmentId?: string;
  employeeType?: string;
  employmentStatus?: string;
  clientName?: string;
  clientId?: string;
  realm?: string;
  companyId?: string;
  company?: string;
}

type DashboardType = 'Admin' | 'Manager' | 'Employee Simple' | 'Employee';

const getUserpilotKey = (): string => {
  const isDevelopment = import.meta.env.MODE === 'development';
  return isDevelopment ? USERPILOT_STG_KEY : USERPILOT_PROD_KEY;
};

const getDashboardType = (user: User): DashboardType => {
  if (user.dashboardType === DashboardTypeEnum.admin) return 'Admin';
  if (user.dashboardType === DashboardTypeEnum.manager) return 'Manager';
  if (user.dashboardType === DashboardTypeEnum.employeeSimple) return 'Employee Simple';
  if (user.dashboardType === DashboardTypeEnum.employee) return 'Employee';
  return 'Employee';
};

const getCompanyId =(companyId: string, clientId: string) => {
  if (companyId && clientId) {
    return `${clientId}-${companyId}`;
  } 
  return companyId || '-';
}

export const initializeUserpilot = (): void => {
  const userpilotKey = getUserpilotKey();
  Userpilot.initialize(userpilotKey);
};

export const identifyUser = async (user: User | null): Promise<void> => {
  if (!user) return;

  try {
    const { basicInformation, workInformation } = await GetEmployeeSingle(user.id, [
      'WorkInformation',
      'UserInformation',
    ]) as { basicInformation: BasicInformation; workInformation: WorkInformation };

    Userpilot.identify(basicInformation.sproutUserId, {
      id: basicInformation.sproutUserId || '-',
      user_id: user.id || '-',
      name: user.userName || '-',
      employee_id: basicInformation.employeeId || '-', 
      department_id: workInformation.departmentId || '-',
      dashboard_type: getDashboardType(user),
      employee_type: workInformation.employeeType || '-',
      employee_status: workInformation.employmentStatus || '-',
      hr_client_name: workInformation.clientName || '-',
      hr_client_id: workInformation.clientId || '-',
      hr_client_url: user.clientUrl || '-',
      realm: workInformation.realm || '-',
      sprout_user_id: basicInformation.sproutUserId || '-',
      company: {
        id: getCompanyId(workInformation.companyId, workInformation.clientId),
        name: workInformation.company || '-',
        hr_company_id: workInformation.companyId || '-',
        hr_client_id: workInformation.clientId || '-',
        hr_client_name: workInformation.clientName || '-',
        hr_client_url: user.clientUrl || '-',
        realm: workInformation.realm || '-',
      },
      created_at: dayjs().toISOString(),
    });
  } catch (error: unknown) {
    console.error('Error identifying user in Userpilot:', error);
  }
};